import { CKEditor } from "@ckeditor/ckeditor5-react";
import PropTypes from "prop-types";
import { StorageController } from "../controllers/storageController";
import { useState, useEffect, useRef } from "react";
import "ckeditor5/ckeditor5.css";
import {
  ClassicEditor,
  AccessibilityHelp,
  Alignment,
  Autoformat,
  AutoImage,
  AutoLink,
  Autosave,
  Bold,
  List,
  CloudServices,
  Essentials,
  FullPage,
  Font,
  GeneralHtmlSupport,
  Heading,
  HtmlComment,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Italic,
  Link,
  LinkImage,
  Paragraph,
  SelectAll,
  TextTransformation,
  Table,
  TableToolbar,
  Undo,
  Underline,
  MediaEmbed,
} from "ckeditor5";
import "../assets/css/components.css";

let TABLE_NAME = null;

class UploadAdapter {
  constructor(loader) {
    this.loader = loader;
  }

  upload() {
    return this.loader.file.then((file) => {
      return new Promise((resolve, reject) => {
        StorageController.add({
          tableName: TABLE_NAME,
          file: file,
          resolve: resolve,
          reject: reject,
        });
      });
    });
  }

  abort() {
    // Handle abort if necessary
  }
}

function CustomUploadAdapterPlugin(editor) {
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
    return new UploadAdapter(loader);
  };
}

export const RichText = ({
  name = "",
  data = "",
  placeholder = "",
  onChange = () => {},
  errorMsg,
  tableName,
  isMandatory = true,
}) => {
  const inputProps = {
    name,
    data,
    onChange,
  };
  const editorContainerRef = useRef(null);
  const editorRef = useRef(null);
  const [isLayoutReady, setIsLayoutReady] = useState(false);

  useEffect(() => {
    setIsLayoutReady(true);
    TABLE_NAME = tableName;
    return () => setIsLayoutReady(false);
  }, []);

  const editorConfig = {
    toolbar: {
      items: [
        "undo",
        "redo",
        "|",
        "heading",
        "|",
        "fontSize",
        "fontFamily",
        "fontColor",
        "fontBackgroundColor",
        "|",
        "bold",
        "italic",
        "underline",
        "|",
        "bulletedList",
        "numberedList",
        "|",
        "alignment",
        "|",
        "insertTable",
        "|",
        "link",
        "mediaEmbed",
        "|",
        "imageUpload",
      ],
      shouldNotGroupWhenFull: false,
    },
    plugins: [
      AccessibilityHelp,
      Alignment,
      Autoformat,
      AutoImage,
      AutoLink,
      Autosave,
      Bold,
      CloudServices,
      Essentials,
      FullPage,
      Font,
      GeneralHtmlSupport,
      Heading,
      HtmlComment,
      ImageBlock,
      ImageCaption,
      ImageInline,
      ImageInsertViaUrl,
      ImageResize,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      ImageUpload,
      Italic,
      List,
      Link,
      LinkImage,
      Paragraph,
      SelectAll,
      TextTransformation,
      Table,
      TableToolbar,
      Undo,
      Underline,
      MediaEmbed,
    ],
    heading: {
      options: [
        {
          model: "paragraph",
          title: "Paragraph",
          class: "ck-heading_paragraph",
        },
        {
          model: "heading1",
          view: "h1",
          title: "Heading 1",
          class: "ck-heading_heading1",
        },
        {
          model: "heading2",
          view: "h2",
          title: "Heading 2",
          class: "ck-heading_heading2",
        },
        {
          model: "heading3",
          view: "h3",
          title: "Heading 3",
          class: "ck-heading_heading3",
        },
        {
          model: "heading4",
          view: "h4",
          title: "Heading 4",
          class: "ck-heading_heading4",
        },
        {
          model: "heading5",
          view: "h5",
          title: "Heading 5",
          class: "ck-heading_heading5",
        },
        {
          model: "heading6",
          view: "h6",
          title: "Heading 6",
          class: "ck-heading_heading6",
        },
      ],
    },
    htmlSupport: {
      allow: [
        {
          name: /^.*$/,
          styles: true,
          attributes: true,
          classes: true,
        },
      ],
    },
    image: {
      toolbar: [
        "toggleImageCaption",
        "imageTextAlternative",
        "|",
        "imageStyle:inline",
        "imageStyle:wrapText",
        "imageStyle:breakText",
        "|",
        "resizeImage",
      ],
    },
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: "https://",
      decorators: {
        toggleDownloadable: {
          mode: "manual",
          label: "Downloadable",
          attributes: {
            download: "file",
          },
        },
      },
    },
    table: {
      contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
    },
    placeholder: placeholder,
    name: name,
    extraPlugins: [CustomUploadAdapterPlugin],
  };

  return (
    <div className="wrapper">
      <label className="form-label">
        {placeholder}
        {isMandatory && <span className="mandatory-field">*</span>}
      </label>
      <div
        className="editor-container editor-container_classic-editor rich-text-field"
        ref={editorContainerRef}
      >
        <div className="editor-container__editor">
          <div ref={editorRef}>
            {isLayoutReady && (
              <CKEditor
                {...inputProps}
                editor={ClassicEditor}
                config={editorConfig}
              />
            )}
          </div>
        </div>
      </div>
      {errorMsg && <span className="error-message">{errorMsg}</span>}
    </div>
  );
};

RichText.propTypes = {
  name: PropTypes.string,
  data: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  errorMsg: PropTypes.string,
  tableName: PropTypes.string,
  isMandatory: PropTypes.bool,
};
